import { Component, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, LoginCredentials } from '../../core/services/auth.service';
import { RoleRoutingService } from '../../services/role-routing.service';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';
import { TranslationService } from '../../services/translation.service';
import { ToastService } from '../../services/toast.service';
import { LanguageService } from '../../services/language.service';
import { getSessionMessage } from '../../core/services/session-messages';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LanguageSelectorComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  isLoading = signal(false);
  errorMessage = signal('');
  sessionExpiredMessage = signal('');
  showPassword = signal(false);

  // Session transfer confirmation state
  showConfirmation = signal(false);
  private sessionTransferToken = signal('');

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly roleRoutingService: RoleRoutingService,
    private readonly toastService: ToastService,
    private readonly languageService: LanguageService,
    public translationService: TranslationService
  ) {}

  ngOnInit() {
    this.initializeForm();

    // Check for session expiration / transfer messages from query params
    this.route.queryParams.subscribe((params) => {
      if (params['sessionExpired'] === 'true') {
        const currentLang = this.languageService.getCurrentLanguage() as 'fr' | 'en';
        const message = getSessionMessage('sessionExpired', currentLang);
        this.sessionExpiredMessage.set(message);
        this.toastService.showWarning(message, 8000);
        this.router.navigate([], {
          queryParams: { sessionExpired: null },
          queryParamsHandling: 'merge',
        });
      }

      if (params['reason'] === 'session_transferred') {
        const message = 'Your session was terminated because you signed in on another device.';
        this.sessionExpiredMessage.set(message);
        this.toastService.showWarning(message, 8000);
        this.router.navigate([], { queryParams: { reason: null }, queryParamsHandling: 'merge' });
      }

      if (params['reason'] === 'inactivity') {
        const currentLang = this.languageService.getCurrentLanguage() as 'fr' | 'en';
        const message = getSessionMessage('sessionExpired', currentLang);
        this.sessionExpiredMessage.set(message);
        this.toastService.showWarning(message, 8000);
        this.router.navigate([], { queryParams: { reason: null }, queryParamsHandling: 'merge' });
      }
    }); // Check if already authenticated
    if (this.authService.isAuthenticated()) {
      this.redirectToDashboard();
    }
  }

  private initializeForm(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  onLogin() {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched();
      this.errorMessage.set('Please fill in all fields correctly');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    const credentials: LoginCredentials = {
      email: this.loginForm.get('email')?.value,
      password: this.loginForm.get('password')?.value,
    };

    this.authService.login(credentials).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.requires_confirmation && response.session_transfer_token) {
          this.sessionTransferToken.set(response.session_transfer_token);
          this.showConfirmation.set(true);
        } else {
          this.redirectToDashboard();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.errorMessage.set(error.message || 'Login failed. Please try again.');
      },
    });
  }

  onConfirmTransfer(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.authService.confirmSessionTransfer(this.sessionTransferToken()).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.showConfirmation.set(false);
        this.redirectToDashboard();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.errorMessage.set(error.message || 'Failed to confirm. Please log in again.');
        this.showConfirmation.set(false);
      },
    });
  }

  onCancelTransfer(): void {
    this.showConfirmation.set(false);
    this.sessionTransferToken.set('');
  }

  private redirectToDashboard(): void {
    const user = this.authService.currentUser();

    if (!user) {
      return;
    }

    // Use RoleRoutingService for consistent navigation
    const success = this.roleRoutingService.navigateToDashboard();

    if (!success) {
      // Fallback navigation
      this.router.navigate(['/dashboard']);
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getters for form validation
  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  // Validation helper methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
      }
      if (field.errors['requiredTrue']) {
        return 'You must accept the terms and conditions';
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  togglePasswordVisibility() {
    this.showPassword.set(!this.showPassword());
  }

  onForgotPassword() {
    // TODO: Implement forgot password functionality
  }
}
